import React, { createContext, useContext, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';

// =============================================================================
// USER TYPE CONTEXT
// =============================================================================

const UserTypeContext = createContext({
  user: null,
  userType: null, // 'company' | 'contractor' | 'both' | null
  isCompanyUser: false,
  isContractor: false,
  isContractorModeAllowed: true, // Company policy - can employee also be contractor?
  organization: null,
  loading: true,
  failed: false, // could not establish identity; not the same as "no org"
});

export const useUserType = () => useContext(UserTypeContext);

// =============================================================================
// USER TYPE PROVIDER
// =============================================================================

export function UserTypeProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // A failed lookup is NOT an answer. See the note on fetchUserInfo.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  /* Who this account is, and what happens when we can't find out.
   *
   * This used to swallow every error and leave `user` null. Null organization
   * means `isCompanyUser` false, which means CompanyRoute redirects to
   * /dashboard/contractor — so one failed request turned a company owner into
   * a contractor for the rest of the session, and every /company/* URL bounced
   * them out of their own account. It is fetched exactly once, on mount, so
   * there was no second chance either.
   *
   * The clickstream caught it end to end. An org signed up on Aug 11, opened
   * /company/postings, was on /dashboard/contractor in the same second, and on
   * /auth/login four minutes later. That is the only company signup that
   * completed in two weeks.
   *
   * So: retry a few times with a rising delay — this runs on phones on mobile
   * data, where one request failing is ordinary — and if it still won't answer,
   * say so rather than guessing. `failed` is what the guards read to tell
   * "definitely not a company" apart from "we don't know yet".
   */
  const fetchUserInfo = async (attempt = 0) => {
    try {
      const res = await fetchWithAuth('/api/accounts/me/');
      setUser(res);
      setFailed(false);
      setLoading(false);
    } catch (err) {
      // 401/403 is a real answer: this session isn't authenticated, and
      // retrying will say the same thing. Everything else is worth another go.
      const authoritative = err?.status === 401 || err?.status === 403;
      if (!authoritative && attempt < 2) {
        setTimeout(() => fetchUserInfo(attempt + 1), 400 * (attempt + 1));
        return;
      }
      console.error('Error fetching user info:', err);
      setFailed(!authoritative);
      setLoading(false);
    }
  };

  // Determine user type
  const organization = user?.organization || null;
  const isCompanyUser = !!organization;
  const isContractorBase = user?.is_contractor || false;
  
  // Check company policy - does their company allow contractor mode?
  // Default: true (allowed). Company can optionally restrict this.
  const companyAllowsContractorMode = organization?.allow_employee_contractor_mode ?? true;
  
  // Effective contractor status considers company policy
  // If user is a company employee AND their company restricts contractor mode, 
  // they can't access contractor features even if they have a contractor profile
  const isContractor = isContractorBase && (companyAllowsContractorMode || !isCompanyUser);
  
  let userType = null;
  if (isCompanyUser && isContractor) {
    userType = 'both';
  } else if (isCompanyUser) {
    userType = 'company';
  } else if (isContractor) {
    userType = 'contractor';
  }

  return (
    <UserTypeContext.Provider value={{
      user,
      userType,
      isCompanyUser,
      isContractor,
      isContractorBase, // The raw is_contractor flag (ignoring policy)
      isContractorModeAllowed: companyAllowsContractorMode,
      organization,
      loading,
      // True when we could not establish who this is. Distinct from "not a
      // company": guards must not act on a question they failed to ask.
      failed,
      refresh: () => fetchUserInfo(0),
    }}>
      {children}
    </UserTypeContext.Provider>
  );
}

// =============================================================================
// USER TYPE GATE COMPONENT
// =============================================================================

/**
 * UserTypeGate - Restrict access based on user type
 * 
 * Usage:
 *   // Only company users can see this
 *   <UserTypeGate allowedTypes={['company', 'both']}>
 *     <CompanyDashboard />
 *   </UserTypeGate>
 * 
 *   // Only contractors can see this (respects company policy)
 *   <UserTypeGate allowedTypes={['contractor', 'both']}>
 *     <ContractorDashboard />
 *   </UserTypeGate>
 */
export function UserTypeGate({ 
  allowedTypes, 
  children, 
  fallback = null,
  redirectTo = null,
}) {
  const { userType, loading, isContractorBase, isContractorModeAllowed, organization } = useUserType();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    );
  }

  const isAllowed = allowedTypes.includes(userType);
  
  if (isAllowed) {
    return children;
  }
  
  // Special case: User has contractor profile but company restricts it
  const isBlockedByCompanyPolicy = 
    allowedTypes.includes('contractor') && 
    isContractorBase && 
    !isContractorModeAllowed;
  
  if (isBlockedByCompanyPolicy) {
    return <ContractorModeRestricted companyName={organization?.name} />;
  }
  
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }
  
  return fallback || <AccessDenied userType={userType} />;
}

// =============================================================================
// COMPANY ONLY GATE
// =============================================================================

/**
 * CompanyOnly - Shorthand for company-only content
 */
export function CompanyOnly({ children, fallback, redirectTo }) {
  return (
    <UserTypeGate 
      allowedTypes={['company', 'both']} 
      fallback={fallback}
      redirectTo={redirectTo}
    >
      {children}
    </UserTypeGate>
  );
}

// =============================================================================
// CONTRACTOR ONLY GATE
// =============================================================================

/**
 * ContractorOnly - Shorthand for contractor-only content
 * Respects company's allow_employee_contractor_mode policy
 */
export function ContractorOnly({ children, fallback, redirectTo }) {
  return (
    <UserTypeGate 
      allowedTypes={['contractor', 'both']} 
      fallback={fallback}
      redirectTo={redirectTo}
    >
      {children}
    </UserTypeGate>
  );
}

// =============================================================================
// CONTRACTOR MODE RESTRICTED MESSAGE
// =============================================================================

function ContractorModeRestricted({ companyName }) {
  return (
    <div className="min-h-screen bg-bg text-ink flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center bg-surface border border-line rounded-2xl p-8">
        <div className="w-16 h-16 bg-yellow-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 className="text-2xl font-display italic text-ink mb-2">Contractor Mode Restricted</h1>
        <p className="text-secondary mb-6">
          {companyName ? `${companyName} has` : 'Your organization has'} disabled contractor
          mode for employees. You cannot access contractor features while employed by this organization.
        </p>
        <p className="text-tertiary text-sm mb-6">
          Contact your administrator if you believe this is an error.
        </p>
        <a
          href="/company/dashboard"
          className="inline-flex items-center justify-center min-h-[40px] px-6 py-3 bg-brand text-white hover:bg-brand-hover rounded-pill font-medium transition-colors"
        >
          Go to Company Dashboard
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// ACCESS DENIED COMPONENT
// =============================================================================

function AccessDenied({ userType }) {
  const getRedirectPath = () => {
    if (userType === 'company') return '/company/dashboard';
    if (userType === 'contractor') return '/dashboard/contractor';
    return '/';
  };

  const getMessage = () => {
    if (userType === 'company') {
      return "This page is for contractors only. You're logged in as a company user.";
    }
    if (userType === 'contractor') {
      return "This page is for company users only. You're logged in as a contractor.";
    }
    return "You don't have access to this page.";
  };

  return (
    <div className="min-h-screen bg-bg text-ink flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center bg-surface border border-line rounded-2xl p-8">
        <div className="w-16 h-16 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-2xl font-display italic text-ink mb-2">Access Restricted</h1>
        <p className="text-secondary mb-6">{getMessage()}</p>
        <a
          href={getRedirectPath()}
          className="inline-flex items-center justify-center min-h-[40px] px-6 py-3 bg-brand text-white hover:bg-brand-hover rounded-pill font-medium transition-colors"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * useRequireCompany - Hook to check company access
 */
export function useRequireCompany() {
  const { isCompanyUser, loading, organization } = useUserType();
  return { 
    isAllowed: isCompanyUser, 
    loading, 
    organization,
    redirectTo: '/dashboard/contractor',
  };
}

/**
 * useRequireContractor - Hook to check contractor access (respects company policy)
 */
export function useRequireContractor() {
  const { isContractor, loading, isContractorModeAllowed } = useUserType();
  return { 
    isAllowed: isContractor, 
    loading,
    isContractorModeAllowed,
    redirectTo: '/company/dashboard',
  };
}

/**
 * useContractorModeAllowed - Hook to check if contractor mode is allowed by company
 */
export function useContractorModeAllowed() {
  const { isContractorModeAllowed, isContractorBase, organization, loading } = useUserType();
  return {
    isAllowed: isContractorModeAllowed,
    hasContractorProfile: isContractorBase,
    isBlockedByCompany: isContractorBase && !isContractorModeAllowed,
    companyName: organization?.name,
    loading,
  };
}

// =============================================================================
// ROUTE WRAPPER COMPONENTS (for App.jsx)
// =============================================================================

/**
 * CompanyRoute - Wrap company routes to restrict access
 */
export function CompanyRoute({ children }) {
  const { isCompanyUser, loading, failed, refresh } = useUserType();

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    );
  }

  /* We asked and got no answer. Redirecting here is what threw company owners
     onto the contractor dashboard and, from there, back to the login page --
     an irreversible bounce out of their own account, decided by one request
     that happened to fail. Offer the retry instead; being told the network is
     unhappy is recoverable, being silently reclassified is not. */
  if (failed) {
    return <CouldNotLoadAccount onRetry={refresh} />;
  }

  if (!isCompanyUser) {
    return <Navigate to="/dashboard/contractor" replace />;
  }

  return children;
}

function CouldNotLoadAccount({ onRetry }) {
  return (
    <div className="min-h-screen bg-bg text-ink flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center bg-surface border border-line rounded-2xl p-8">
        <h1 className="text-2xl font-display italic text-ink mb-2">We couldn't load your account</h1>
        <p className="text-secondary mb-6">
          That's a connection problem on our side, not anything wrong with your
          account. Everything is still here.
        </p>
        <button
          onClick={onRetry}
          className="inline-flex items-center justify-center min-h-[40px] px-6 py-3 bg-brand text-white hover:bg-brand-hover rounded-pill font-medium transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

/**
 * ContractorRoute - Wrap contractor routes to restrict access
 * Respects company's allow_employee_contractor_mode policy
 */
export function ContractorRoute({ children }) {
  const { isContractor, isContractorBase, isContractorModeAllowed, organization, loading, failed, refresh } = useUserType();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    );
  }

  // Same rule as CompanyRoute: never redirect on a question we failed to ask.
  if (failed) {
    return <CouldNotLoadAccount onRetry={refresh} />;
  }

  // Blocked by company policy
  if (isContractorBase && !isContractorModeAllowed) {
    return <ContractorModeRestricted companyName={organization?.name} />;
  }

  if (!isContractor) {
    return <Navigate to="/company/dashboard" replace />;
  }
  
  return children;
}

export default UserTypeGate;